import Phaser from 'phaser';
import * as localForage from 'localforage';

import { EntityConfig, Flags, SerializedSharedState, SharedStateModification } from 'tower/types';
import { Direction } from 'tower/util';
import * as C from 'tower/constants';

/** Represents state that is shared between all running instances of the game. */
export default class SharedState extends Phaser.Events.EventEmitter {
  items: Set<string> = new Set(['sword']);
  solvedRooms: Set<string> = new Set();
  foundKeys: Set<string> = new Set();
  keyCount: number = 0;
  roomItems: Record<string, EntityConfig[]> = {};
  flags: Flags = {
    startedGame: false,
    disableFlashing: false,
    blueRodPlaced: false,
    redRodPlaced: false,
  };
  openedDoors: Record<string, { [key in Direction]: boolean }> = {};

  toJSON(): SerializedSharedState {
    return {
      items: Array.from(this.items),
      solvedRooms: Array.from(this.solvedRooms),
      foundKeys: Array.from(this.foundKeys),
      keyCount: this.keyCount,
      roomItems: this.roomItems,
      flags: this.flags,
      openedDoors: this.openedDoors,
    };
  }

  reset() {
    this.items = new Set(['sword']);
    this.solvedRooms = new Set();
    this.foundKeys = new Set();
    this.keyCount = 0;
    this.roomItems = {};
    this.flags = { ...this.flags, blueRodPlaced: false, redRodPlaced: false, startedGame: false };
    this.openedDoors = {};
  }

  async hasSave() {
    return (await localForage.getItem(C.SHAREDSTORAGE_KEY)) !== null && this.flags.startedGame;
  }

  applyModification(mod: SharedStateModification, emitApplyEvent = true) {
    switch (mod.type) {
      case 'addItem':
        this.items.add(mod.item);
        this.emit('itemchange', this.items);

        // Remove any lingering room items matching what we just picked up
        let droppedItemRooms = new Set<string>();
        for (const key of Object.keys(this.roomItems)) {
          const items = this.roomItems[key];
          for (let k = 0; k < items.length; k++) {
            if (items[k].type === mod.item) {
              droppedItemRooms.add(key);
              items.splice(k, 1);
              k--;
            }
          }
        }
        for (const key of droppedItemRooms) {
          this.emit('roomitemchange', key, this.roomItems[key]);
        }
        break;

      case 'removeItem':
        this.items.delete(mod.item);
        this.emit('itemchange', this.items);
        break;

      case 'addKey':
        this.foundKeys.add(mod.room);
        this.keyCount++;
        this.emit('foundkeychange', this.foundKeys);
        this.emit('keycountchange', this.keyCount);
        break;

      case 'useKey':
        this.keyCount = Math.max(this.keyCount - 1, 0);
        this.emit('keycountchange', this.keyCount);
        break;

      case 'solveRoom':
        this.solvedRooms.add(mod.room);
        this.emit('solvedroomschange', this.solvedRooms);
        break;

      case 'addRoomItem': {
        if (mod.roomItem.id === undefined) {
          throw new Error('Room items must have an ID to persist');
        }
        const roomItems = this.roomItems[mod.room] ?? [];
        if (!roomItems.some((i) => i.id === mod.roomItem.id)) {
          roomItems.push(mod.roomItem);
          this.roomItems[mod.room] = roomItems;
          this.emit('roomitemchange', mod.room, roomItems);
        }
        break;
      }

      case 'removeRoomItem': {
        let roomItems = this.roomItems[mod.room];
        if (roomItems === undefined) {
          break;
        }
        roomItems = roomItems.filter((i) => i.id !== mod.itemId);
        this.roomItems[mod.room] = roomItems;
        this.emit('roomitemchange', mod.room, roomItems);
        break;
      }

      case 'setFlag':
        this.flags[mod.flag] = mod.value;
        this.emit('flagchange', this.flags);
        break;

      case 'setOpenedDoor':
        const openedDoors = this.openedDoors[mod.room] ?? { up: false, left: false, right: false, down: false };
        openedDoors[mod.direction] = mod.value;
        this.openedDoors[mod.room] = openedDoors;
        this.emit('openeddoorschange', this.openedDoors);
        break;
    }

    if (emitApplyEvent) {
      this.emit('applymodification', mod);
    }
  }

  applySerialized(serialized: SerializedSharedState) {
    this.items = new Set(serialized.items);
    this.solvedRooms = new Set(serialized.solvedRooms);
    this.foundKeys = new Set(serialized.foundKeys);
    this.keyCount = serialized.keyCount;
    this.roomItems = structuredClone(serialized.roomItems);
    this.flags = structuredClone(serialized.flags);
    this.openedDoors = structuredClone(serialized.openedDoors);
  }
}
